import Humanize from "humanize-plus";

import * as Validators from "src/utils/validators";
import { FETCH_STATES } from "src/redux/Tx/constants";
import { setDateFormat } from "src/utils";
import { intOrDash, numberOrDash } from "src/utils/number";
import { CurrencyFormatter } from "src/utils/CurrencyConversionService";
import { COUNTRY_OPTIONS } from "src/redux/MarketData/constants";
import * as Formatters from "src/components/common/AgGrid/formatters";
import getHeaderValue from "src/components/common/AgGrid/headerGetter";

import styles from "src/styles/agGridCustom.module.scss";
import {
    HUMANIZED_INTERMEDIARY,
    HUMANIZED_TX_TYPES,
} from "src/redux/Confirmations/constants";
import empty from "is-empty";
import {
    TX_PURPOSE_SELECT_LABELS,
    TX_PURPOSE_SELECT_OPTIONS,
} from "src/utils/validators";
import DropdownItemRenderer from "src/components/common/AgGrid/DropdownItemRenderer";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { store } from "src/index";
import { isEmpty } from "lodash";

export const RowClassRules = {
    [styles.agRowError]: (params) => !Validators.isTakeRowValid(params.data),
};

export const CommonColumnDefs = {
    resizable: true,
    suppressMenu: true,
    sortable: true,
    tooltipComponent: "customTooltip",
};

export const valueFormatter = (params) => {
    if (!params.data) return "";
    const field = params.colDef.field;

    switch (field) {
        case "Country":
            return params.data[field] || "-";
        case "PreviousClose":
            return params.data[field]
                ? CurrencyFormatter.format(
                      params.data[field],
                      params.context.preferredCcy ||
                          COUNTRY_OPTIONS[params.data.Country]?.currency
                  )
                : "-";
        case "Amount":
            return intOrDash({
                value: params.data[field],
                replacement: "ERROR",
            });
        case "AvailableQuantity":
            if (
                (!params.data[field] && params.data[field] !== 0) ||
                isNaN(params.data[field])
            ) {
                return "ERROR";
            }
            return Humanize.intComma(params.data[field]);
        case "Dividend":
            if (!params.data[field] && params.data[field] !== 0) return "-";
            if (isNaN(params.data[field])) return "ERROR";
            return Humanize.formatNumber(params.data[field], 2);
        case "Callable":
            return params.data[field] ? "Y" : "N";
        case "Utilization":
            return numberOrDash({ value: params.data[field] });
        case "BenchmarkFee":
            return numberOrDash({ value: params.data[field] });
        case "ValueDate":
            return setDateFormat(params.data[field], "/") || "-";
        case "LoanType":
            if (params.data[field] === "LOAN") return "Regular";
            else if (params.data[field] === "BID") return "Bid";
            else return "ERROR";
        default:
            return params.data[params.colDef.field];
    }
};

export const getTakeAvailabilityColumnDefs = () => {
    const {
        "enable-purpose": enabledPurpose,
        "enable-90-day-max-maturity-date": enable90DayMaxMaturity,
    } = SettingsSelectors.FeatureFlags(store.getState());

    return [
        {
            headerName: "Num",
            colId: "Create Num",
            valueGetter: (params) => params.node.rowIndex + 1,
            minWidth: 70,
            flex: 7,
            sortable: false,
            cellClass: [styles.agCenterAlign],
        },
        {
            headerName: "Lender",
            field: "Lender",
            colId: "Create Lender",
            minWidth: 80,
            flex: 8,
        },
        {
            headerName: "BBG Ticker",
            field: "BbgCode",
            colId: "BBG code",
            minWidth: 80,
            flex: 8,
        },
        {
            headerName: "Ticker",
            field: "SecurityId",
            colId: "Create Ticker",
            minWidth: 80,
            flex: 8,
            cellClass: ["data-type-string"],
        },
        {
            headerName: "Security Name",
            field: "Name",
            colId: "Create Sec Name",
            minWidth: 120,
            flex: 12,
        },
        {
            headerName: "Security Name (Local)",
            field: "LocalName",
            colId: "Create Local Name",
            minWidth: 120,
            flex: 12,
        },
        {
            headerName: "Benchmark Fee (bps)",
            field: "BenchmarkFee",
            colId: "Create Benchmark Fee",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerName: "Sent Qty",
            field: "Quantity",
            colId: "Create Qty",
            minWidth: 90,
            flex: 9,
            editable: (params) => true,
            cellClass: [styles.agEditable, styles.agRightAlign],
            cellClassRules: {
                [styles.agCellError]: (params) =>
                    !Validators.isQuantityValid(
                        params.value,
                        params.data.Country
                    ),
            },
            valueParser: (params) =>
                Validators.parseIntToSizeLimit(params.newValue),
            valueFormatter: (params) =>
                Formatters.formatQuantity(params.value, params.data.Country),
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "quantity" },
        },
        {
            headerName: "Sent Rate (%)",
            field: "Rate",
            colId: "Create Rate",
            minWidth: 90,
            flex: 9,
            editable: (params) => true,
            cellClass: [styles.agEditable, styles.agRightAlign],
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "availableRate" },
            cellClassRules: {
                [styles.agCellError]: (params) =>
                    params.value && !Validators.isRateValid(params.value),
            },
            valueParser: (params) =>
                Validators.isRateValid(params.newValue)
                    ? parseFloat(params.newValue).toFixed(2)
                    : params.newValue,
            valueFormatter: (params) =>
                Formatters.formatRate(params.value, true),
        },
        {
            headerName: "Received Qty",
            field: "AvailableQuantity",
            colId: "Create Available Quantity",
            minWidth: 80,
            flex: 8,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.textOrange]: (params) =>
                    params.value < params.data.Quantity,
            },
            valueFormatter: (params) => valueFormatter(params),
            tooltipField: "AvailableQuantity",
            tooltipValueGetter: (params) => ({ value: params.value }),
            tooltipComponentParams: { type: "availableQty" },
        },
        {
            headerName: "Received Rate (%)",
            field: "AvailableRate",
            colId: "Create Available Rate",
            minWidth: 80,
            flex: 8,
            cellClass: [styles.agRightAlign],
            valueFormatter: (params) =>
                Formatters.formatRate(params.value, true),
        },
        {
            headerName: "Utilization",
            field: "Utilization",
            colId: "Create Utilization",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            cellClassRules: {
                [styles.agCellLoading]: (params) =>
                    params.data.marketData.status === FETCH_STATES.FETCHING,
            },
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerName: "V/D",
            field: "ValueDate",
            colId: "Create ValueDate",
            minWidth: 100,
            flex: 10,
            editable: (params) => true,
            cellEditor: "datePicker",
            cellEditorPopup: true,
            cellClass: [styles.agEditable, styles.agCenterAlign],
            cellClassRules: {
                [styles.agCellError]: (params) =>
                    params.value &&
                    !Validators.isDateValid(
                        params.value,
                        params.data.TradeableDates
                    ),
            },
            tooltipField: "ValueDate",
            tooltipComponentParams: { type: "txCreateVD" },
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerName: "Records Type",
            field: "LoanType",
            colId: "Create Loan Type",
            minWidth: 130,
            flex: 13,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) => valueFormatter(params),
            tooltipValueGetter: (params) => ({
                value: params.value,
            }),
            tooltipComponentParams: { type: "availabilityLoanType" },
        },
        {
            headerName: "Intermediary",
            field: "Intermediary",
            colId: "Details Intermediary",
            minWidth: 150,
            flex: 15,
            valueFormatter: (params) =>
                HUMANIZED_INTERMEDIARY[params.data?.Intermediary] || "-",
            cellClass: [styles.agCenterAlign],
        },
        {
            headerName: "Type",
            field: "TxType",
            colId: "Details TX Type",
            minWidth: 150,
            flex: 15,
            valueFormatter: (params) =>
                HUMANIZED_TX_TYPES[params.data?.TxType] || "-",
            cellClass: [styles.agCenterAlign],
        },
        {
            headerName: "Maturity",
            field: "Maturity",
            colId: "Create Maturity",
            minWidth: 100,
            flex: 10,
            editable: (params) => true,
            cellEditor: "datePicker",
            cellEditorPopup: true,
            cellClass: [styles.agEditable, styles.agCenterAlign],
            cellClassRules: {
                [styles.agCellError]: (params) =>
                    params.value &&
                    !Validators.isMaxMaturityValid(
                        params.data,
                        enable90DayMaxMaturity
                    ),
            },
            valueFormatter: (params) => setDateFormat(params.value, "/") || "-",
            tooltipField: "Maturity",
            tooltipComponentParams: { type: "Maturity" },
        },
        {
            headerName: "Days to Maturity",
            colId: "Create Days to Maturity",
            field: "Term",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agCenterAlign],
            valueFormatter: (params) =>
                params.value ? `${params.value} days` : "-",
        },
        enabledPurpose
            ? {
                  headerName: "Purpose",
                  field: "Purpose",
                  colId: "Create Purpose",
                  editable: true,
                  cellClass: [styles.agEditable],
                  minWidth: 110,
                  flex: 11,
                  cellClassRules: {
                      [styles.agCellError]: (params) =>
                          !Validators.isMarketDataValid(params.data.marketData),
                      [styles.agCellLoading]: (params) =>
                          params.data.marketData.status ===
                          FETCH_STATES.FETCHING,
                  },
                  cellEditor: "agRichSelectCellEditor",
                  cellEditorPopup: true,
                  cellEditorParams: () => ({
                      values: TX_PURPOSE_SELECT_OPTIONS(),
                      formatValue: TX_PURPOSE_SELECT_LABELS,
                      cellRenderer: DropdownItemRenderer,
                  }),
                  valueFormatter: (params) => {
                      return TX_PURPOSE_SELECT_LABELS(params.value);
                  },
              }
            : {},
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Value (Sent)",
            field: "Amount",
            colId: "Create Value (Request)",
            minWidth: 120,
            flex: 12,
            cellClass: [styles.agRightAlign],
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerName: "Country",
            field: "Country",
            colId: "Create Country",
            minWidth: 80,
            flex: 8,
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerValueGetter: (params) => getHeaderValue(params),
            headerName: "Prev Close",
            field: "PreviousClose",
            colId: "Create Prev Close",
            minWidth: 100,
            flex: 10,
            cellClass: [styles.agRightAlign],
            valueFormatter: (params) => valueFormatter(params),
        },
        {
            headerName: "Delete",
            field: "Delete",
            colId: "Create Delete",
            width: 60,
            cellRenderer: "deleteButton",
        },
    ].filter((col) => !isEmpty(col));
};
